import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuthFetch } from "../hooks/useAuthFetch";
import "../stili/PresenzaGenitore.css";

const API_URL = process.env.REACT_APP_API_URL;

function formatDate(value) {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleDateString("it-IT");
  } catch {
    return value;
  }
}

function formatAllenamentoLabel(allenamento) {
  if (!allenamento) return "-";

  const data = allenamento.dataAllenamento
    ? formatDate(allenamento.dataAllenamento)
    : "-";

  const oraInizio = allenamento.oraInizio || "";
  const oraFine = allenamento.oraFine || "";
  const campo = allenamento.campo || "";

  let label = data;

  if (oraInizio) {
    label += ` - ${oraInizio}`;
    if (oraFine) label += ` / ${oraFine}`;
  }

  if (campo) {
    label += ` - ${campo}`;
  }

  return label;
}

export default function PresenzaGenitore() {
  const authFetch = useAuthFetch();
  const navigate = useNavigate();

  const [figli, setFigli] = useState([]);
  const [figlioSelezionatoId, setFiglioSelezionatoId] = useState("");
  const [allenamenti, setAllenamenti] = useState([]);
  const [idAllenamentoSelezionato, setIdAllenamentoSelezionato] = useState("");
  const [presenze, setPresenze] = useState([]);

  const [loadingFigli, setLoadingFigli] = useState(false);
  const [loadingAllenamenti, setLoadingAllenamenti] = useState(false);
  const [loadingPresenze, setLoadingPresenze] = useState(false);
  const [saving, setSaving] = useState(false);

  const figlioSelezionato = useMemo(
    () =>
      figli.find((f) => String(f.idGiocatore) === String(figlioSelezionatoId)) ||
      null,
    [figli, figlioSelezionatoId]
  );

  const parseErrorMessage = async (res, fallbackMessage) => {
    try {
      const contentType = res.headers.get("content-type") || "";

      if (contentType.includes("application/json")) {
        const data = await res.json();
        return data?.message || data?.error || data?.details || fallbackMessage;
      }

      const text = await res.text();
      return text || fallbackMessage;
    } catch {
      return fallbackMessage;
    }
  };

  const fetchFigli = useCallback(async () => {
    setLoadingFigli(true);

    try {
      const res = await authFetch(`${API_URL}/api/genitore/dashboard/figli`);

      if (!res.ok) {
        throw new Error(
          await parseErrorMessage(res, "Errore nel caricamento dei figli")
        );
      }

      const data = await res.json().catch(() => []);
      const lista = Array.isArray(data) ? data : [];

      setFigli(lista);

      if (lista.length > 0) {
        setFiglioSelezionatoId(String(lista[0].idGiocatore));
      } else {
        setFiglioSelezionatoId("");
        setAllenamenti([]);
        setIdAllenamentoSelezionato("");
        setPresenze([]);
      }
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Errore nel caricamento dei figli");
      setFigli([]);
      setFiglioSelezionatoId("");
      setAllenamenti([]);
      setIdAllenamentoSelezionato("");
      setPresenze([]);
    } finally {
      setLoadingFigli(false);
    }
  }, [authFetch]);

  const fetchAllenamenti = useCallback(
    async (idCategoria) => {
      if (!idCategoria) {
        setAllenamenti([]);
        setIdAllenamentoSelezionato("");
        setPresenze([]);
        return;
      }

      setLoadingAllenamenti(true);

      try {
        const res = await authFetch(
          `${API_URL}/api/genitore/presenze/allenamenti/categoria/${idCategoria}`
        );

        if (!res.ok) {
          throw new Error(
            await parseErrorMessage(res, "Errore nel caricamento allenamenti")
          );
        }

        const data = await res.json().catch(() => []);
        const lista = Array.isArray(data) ? data : [];

        const normalizzati = lista.map((a) => ({
          ...a,
          id: a.id ?? a.idAllenamento ?? null,
          dataAllenamento: a.dataAllenamento ?? a.data_allenamento ?? null,
          oraInizio: a.oraInizio ?? a.ora_inizio ?? null,
          oraFine: a.oraFine ?? a.ora_fine ?? null,
          idCategoria: a.idCategoria ?? a.id_categoria ?? null,
        }));

        const oggi = new Date();
        oggi.setHours(0, 0, 0, 0);

        const futuri = normalizzati.filter((a) => {
          if (!a?.dataAllenamento) return false;
          const dataAllenamento = new Date(a.dataAllenamento);
          dataAllenamento.setHours(0, 0, 0, 0);
          return dataAllenamento >= oggi;
        });

        setAllenamenti(futuri);
        setIdAllenamentoSelezionato("");
        setPresenze([]);
      } catch (err) {
        console.error(err);
        toast.error(err.message || "Errore nel caricamento allenamenti");
        setAllenamenti([]);
        setIdAllenamentoSelezionato("");
        setPresenze([]);
      } finally {
        setLoadingAllenamenti(false);
      }
    },
    [authFetch]
  );

  const fetchPresenze = useCallback(
    async (idAllenamento, idGiocatore) => {
      if (!idAllenamento || !idGiocatore) {
        setPresenze([]);
        return;
      }

      setLoadingPresenze(true);

      try {
        const res = await authFetch(
          `${API_URL}/api/genitore/presenze/allenamento/${idAllenamento}`
        );

        if (!res.ok) {
          throw new Error(
            await parseErrorMessage(res, "Errore nel caricamento presenze")
          );
        }

        const data = await res.json().catch(() => []);
        const lista = Array.isArray(data) ? data : [];

        const soloFiglio = lista.filter(
          (p) =>
            String(
              p.idGiocatore ??
                p.giocatoreId ??
                p.id_giocatore ??
                p?.giocatore?.idGiocatore
            ) === String(idGiocatore)
        );

        setPresenze(soloFiglio);
      } catch (err) {
        console.error(err);
        toast.error(err.message || "Errore nel caricamento presenze");
        setPresenze([]);
      } finally {
        setLoadingPresenze(false);
      }
    },
    [authFetch]
  );

  useEffect(() => {
    fetchFigli();
  }, [fetchFigli]);

  useEffect(() => {
    if (!figlioSelezionato) {
      setAllenamenti([]);
      setIdAllenamentoSelezionato("");
      setPresenze([]);
      return;
    }

    fetchAllenamenti(
      figlioSelezionato.idCategoria || figlioSelezionato.categoriaId || null
    );
  }, [figlioSelezionato, fetchAllenamenti]);

  useEffect(() => {
    if (!idAllenamentoSelezionato || !figlioSelezionatoId) {
      setPresenze([]);
      return;
    }

    fetchPresenze(idAllenamentoSelezionato, figlioSelezionatoId);
  }, [idAllenamentoSelezionato, figlioSelezionatoId, fetchPresenze]);

  const togglePresenza = (idPresenza) => {
    setPresenze((prev) =>
      prev.map((p) =>
        p.id === idPresenza ? { ...p, presente: !p.presente } : p
      )
    );
  };

  const handleSubmit = async () => {
    if (!figlioSelezionatoId) {
      toast.warning("Seleziona un figlio.");
      return;
    }

    if (!idAllenamentoSelezionato) {
      toast.warning("Seleziona un allenamento.");
      return;
    }

    if (presenze.length === 0) {
      toast.warning("Nessuna presenza da aggiornare.");
      return;
    }

    setSaving(true);

    try {
      const payload = {
        idAllenamento: Number(idAllenamentoSelezionato),
        items: presenze.map((p) => ({
          idGiocatore: p.idGiocatore,
          presente: !!p.presente,
        })),
      };

      const res = await authFetch(`${API_URL}/api/genitore/presenze/batch`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        throw new Error(
          await parseErrorMessage(res, "Errore nel salvataggio presenza")
        );
      }

      toast.success("Presenza aggiornata correttamente");
      fetchPresenze(idAllenamentoSelezionato, figlioSelezionatoId);
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Errore di comunicazione con il server");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="presenza-genitore-page" translate="no">
      <div className="presenza-genitore-container">
        <h2 className="presenza-genitore-title">Presenza Allenamento</h2>

        <div className="presenza-genitore-row">
          <label>Figlio</label>
          <select
            value={figlioSelezionatoId}
            onChange={(e) => setFiglioSelezionatoId(e.target.value)}
            disabled={loadingFigli || figli.length === 0 || saving}
          >
            <option value="">
              {loadingFigli ? "Caricamento figli..." : "Seleziona figlio"}
            </option>
            {figli.map((figlio) => (
              <option key={figlio.idGiocatore} value={figlio.idGiocatore}>
                {figlio.cognome} {figlio.nome}
                {figlio.categoria ? ` - ${figlio.categoria}` : ""}
              </option>
            ))}
          </select>
        </div>

        {figlioSelezionato?.categoria && (
          <div className="presenza-genitore-row">
            <label>Categoria</label>
            <input type="text" value={figlioSelezionato.categoria} disabled />
          </div>
        )}

        {figlioSelezionato && (
          <div className="presenza-genitore-row">
            <label>Allenamento</label>
            <select
              value={idAllenamentoSelezionato}
              onChange={(e) => setIdAllenamentoSelezionato(e.target.value)}
              disabled={loadingAllenamenti || saving || allenamenti.length === 0}
            >
              <option value="">
                {loadingAllenamenti
                  ? "Caricamento allenamenti..."
                  : allenamenti.length === 0
                    ? "Nessun allenamento disponibile"
                    : "Seleziona allenamento"}
              </option>
              {allenamenti.map((a) => (
                <option key={a.id} value={a.id}>
                  {formatAllenamentoLabel(a)}
                </option>
              ))}
            </select>
          </div>
        )}

        {(loadingFigli || loadingAllenamenti || loadingPresenze) && (
          <div className="presenza-genitore-info-box">Caricamento in corso...</div>
        )}

        {!loadingPresenze && idAllenamentoSelezionato && presenze.length === 0 && (
          <div className="presenza-genitore-info-box">
            Nessuna presenza disponibile per l’allenamento selezionato.
          </div>
        )}

        {presenze.length > 0 && idAllenamentoSelezionato && (
          <>
            <div className="presenza-genitore-table-container">
              <table className="presenza-genitore-table">
                <thead>
                  <tr>
                    <th>Giocatore</th>
                  </tr>
                </thead>
                <tbody>
                  {presenze.map((p) => (
                    <tr key={p.id}>
                      <td>
                        <div className="presenza-genitore-player-box">
                          <div className="presenza-genitore-player-name">
                            <span className="presenza-genitore-player-cognome">
                              {p.cognomeGiocatore || "-"}
                            </span>
                            <span className="presenza-genitore-player-nome">
                              {p.nomeGiocatore || "-"}
                            </span>
                          </div>

                          <div className="presenza-genitore-checkbox-cell">
                            <span className="presenza-genitore-checkbox-label">
                              Presente
                            </span>
                            <input
                              type="checkbox"
                              checked={!!p.presente}
                              onChange={() => togglePresenza(p.id)}
                              disabled={saving}
                            />
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="presenza-genitore-button-row">
              <button
                type="button"
                className="presenza-genitore-btn-save"
                onClick={handleSubmit}
                disabled={saving}
              >
                {saving ? "Salvataggio..." : "Salva Presenza"}
              </button>

              <button
                type="button"
                className="presenza-genitore-btn-back"
                onClick={() => navigate("/gestionale2")}
                disabled={saving}
              >
                Torna al Gestionale
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}